import * as React from 'react';
import ProducerInterface from '@app/Interface/ProducerInterface';
import ProducerHelper from '@app/Service/ProducerHelper';
import { ContextSelector, ContextSelectorFooter, ContextSelectorItem, PageHeaderToolsItem, spinnerSize } from '@patternfly/react-core';
import User from '@app/Interface/UserInterface';
import ItemLoader from '../ItemLoader';

interface FpuContextSelectorProps {
    currentUser: User;
    onFpuChange: (fpu: ProducerInterface) => void;
}


const FpuContextSelector: React.FC<FpuContextSelectorProps> = ({ currentUser, onFpuChange }) => {

    const [isOpen, setIsOpen] = React.useState(false);

    // on first load, use the user's currently selected FPU
    const defaultSelected = currentUser.currentFpu.producerId + " - " + currentUser.currentFpu.alphaName;

    const [selected, setSelected] = React.useState(defaultSelected);

    const [searchTerm, setSearchTerm] = React.useState<string | undefined>(undefined);

    const initialItems: ProducerInterface[] = [];

    const [filteredItems, setFilteredItems] = React.useState(initialItems);

    // get items when the page is loading, so set to true
    const [getItems, setGetItems] = React.useState(true);

    const onToggle = (event: React.SyntheticEvent, isOpen: boolean) => {

        setIsOpen(isOpen);

    }

    const onSelect = (event: React.SyntheticEvent, value: React.ReactNode) => {

        const selectedItem = value as React.ReactElement;

        let fpuId = Number(selectedItem.props.id) ?? 0;

        const textValue = String(selectedItem.props.children) ?? "";

        const selectedValue = `${fpuId} - ${textValue}`;

        if (Number.isNaN(fpuId)) {
            fpuId = 0;
        }

        setSelected(selectedValue);

        setIsOpen(!isOpen);

        const selectedFpu = filteredItems.find(item => item.producerId === fpuId);

        if (selectedFpu) {
            onFpuChange(selectedFpu);
        }

    }

    const onSearchInputChange = (value: string) => {
        setSearchTerm(value);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const onSearchButtonClick = (event?: React.SyntheticEvent<HTMLButtonElement, Event> | undefined) => {

        // clear the current set of items
        setFilteredItems([]);

        setGetItems(true);

    }

    // get the producers
    React.useEffect(() => {

        const controller = new AbortController();

        let mounted = true;

        const getProducers = async () => {

            const producers = await ProducerHelper.getProducers(controller, 10, searchTerm);

            if (mounted) {
                setFilteredItems(producers);

                setGetItems(false);
            }

        }

        if (getItems) {
            getProducers();
        }

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, [getItems, searchTerm]);

    // check to see if the current fpu changed, if so, select it.
    React.useEffect(() => {

        if (selected !== currentUser.currentFpu.alphaName) {

            // on first load, use the user's currently selected FPU
            const currentFpu = currentUser.currentFpu.producerId + " - " + currentUser.currentFpu.alphaName;

            setSelected(currentFpu);

        }

    }, [currentUser.currentFpu.alphaName, currentUser.currentFpu.producerId, selected]);

    /**
     * render the drop-down tool. 
     * NOTE: patternfly breakpoints are based on minimum width. Think opposite
     *       set default width to apply to all break-points. Then set desired 
     *       width for larger breakpoints. In this case. For the drop-down tool. 
     *       set all widths to 33%. Then breakpoints `md` or higher, set width to 100%
     */
     return (
        <PageHeaderToolsItem>
            <ContextSelector
                toggleText={selected}
                onSearchInputChange={onSearchInputChange}
                footer={<ContextSelectorFooter>
                    <p>
                        <em>Displaying a maximum of 10 producers. Use the search box to refine your results.</em>
                    </p>
                </ContextSelectorFooter>}
                isOpen={isOpen}
                searchInputValue={searchTerm}
                onToggle={onToggle}
                onSelect={onSelect}
                onSearchButtonClick={onSearchButtonClick}
                screenReaderLabel="Selected FPU: "
            >
                {getItems
                    ?
                    <div role="menuitem">
                        <ContextSelectorItem>
                            <ItemLoader showTitle={false} size={spinnerSize.md} />
                        </ContextSelectorItem>
                    </div>
                    :
                    <>
                        {filteredItems.map(item => {
                            return (
                                <div role="menuitem" key={item.producerId}>
                                    <ContextSelectorItem>
                                        <span id={item.producerId.toString()}>{` ${item.producerId} - ${item.alphaName}`}</span>
                                    </ContextSelectorItem>
                                </div>
                            );
                        })}
                    </>
                }

            </ContextSelector>
        </PageHeaderToolsItem>
    );

}

export default FpuContextSelector;
